import React, { useMemo } from 'react';
import { TrackerCatalyst, TrackerConference, TrackerSavedView } from '../../types';
import TrackerIcon from './TrackerIcon';
import { CATALYST_TYPES, COMPANY_TIERS, SIGNALS, TrackerFilterState, TrackerSort, normHist, toggleWhen } from './helpers';

interface Props {
  rows: TrackerCatalyst[];
  conferences: TrackerConference[];
  filters: TrackerFilterState;
  onChange: (next: Partial<TrackerFilterState>) => void;
  onReset: () => void;
  sort: TrackerSort;
  onSort: (s: TrackerSort) => void;
  view: 'list' | 'calendar';
  onView: (v: 'list' | 'calendar') => void;
  savedViews: TrackerSavedView[];
  /** "" = None, "v<i>" = that saved view (also what's currently shown selected). */
  savedViewValue: string;
  /** "" = None, "v<i>" = that saved view, "__save"/"__del" = the two action options. */
  onSelectSavedView: (value: string) => void;
}

/** The `.controls` filter bar — same markup/classes as catalyst_tracker/templates/catalyst_tracker/index.html's
 * `<section class="controls">`, just driven by React state instead of imperative DOM updates. */
const CatalystFilterBar: React.FC<Props> = ({
  rows, conferences, filters, onChange, onReset, sort, onSort, view, onView, savedViews, savedViewValue, onSelectSavedView,
}) => {
  const companies = useMemo(() => [...new Set(rows.map((r) => r.company).filter(Boolean))].sort() as string[], [rows]);
  const histologies = useMemo(() => [...new Set(rows.map((r) => normHist(r.histology)).filter(Boolean))].sort(), [rows]);

  return (
    <section className="controls" aria-label="Filters">
      <div className="seg" role="group" aria-label="Time">
        <button
          type="button"
          aria-pressed={filters.when === 'all' || filters.when === 'upcoming'}
          title="Upcoming"
          aria-label="Upcoming"
          onClick={() => onChange({ when: toggleWhen(filters.when, 'upcoming') })}
        >
          <TrackerIcon name="upcoming" />
        </button>
        <button
          type="button"
          aria-pressed={filters.when === 'all' || filters.when === 'past'}
          title="Past"
          aria-label="Past"
          onClick={() => onChange({ when: toggleWhen(filters.when, 'past') })}
        >
          <TrackerIcon name="history" />
        </button>
      </div>

      <div className="seg" role="group" aria-label="View">
        <button type="button" aria-pressed={view === 'list'} title="List by month" aria-label="List by month" onClick={() => onView('list')}>
          <TrackerIcon name="list" />
        </button>
        <button type="button" aria-pressed={view === 'calendar'} title="Month calendar" aria-label="Month calendar" onClick={() => onView('calendar')}>
          <TrackerIcon name="cal" />
        </button>
      </div>

      <button
        type="button"
        className="ib"
        aria-pressed={filters.delayedOnly || filters.hideDelayed}
        title={`${filters.delayedOnly ? 'Now showing only delayed or overdue catalysts. Click to hide them instead.' : filters.hideDelayed ? 'Now hiding delayed or overdue catalysts. Click to show all catalysts again.' : 'Delayed / overdue filter — now showing all catalysts. Click to show only delayed or overdue ones.'} Delayed = the company pushed its timing back. Overdue = the guided window has closed with no news yet.`}
        onClick={() => {
          if (filters.delayedOnly) onChange({ delayedOnly: false, hideDelayed: true });
          else if (filters.hideDelayed) onChange({ hideDelayed: false });
          else onChange({ delayedOnly: true });
        }}
      >
        <TrackerIcon name={filters.hideDelayed ? 'eyeoff' : 'alert'} />
      </button>
      <button type="button" className="ib" aria-pressed={filters.watchOnly} title="Only my favorites" onClick={() => onChange({ watchOnly: !filters.watchOnly })}>
        <TrackerIcon name="star" />
      </button>
      <button type="button" className="ib" aria-pressed={filters.highOnly} title="Only high-impact catalysts" onClick={() => onChange({ highOnly: !filters.highOnly })}>
        <TrackerIcon name="flame" />
      </button>

      <div className="field">
        <label htmlFor="fCompany">Company</label>
        <select id="fCompany" value={filters.company} onChange={(e) => onChange({ company: e.target.value })}>
          <option value="">All</option>
          {companies.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <div className="field">
        <label htmlFor="fHist">Tumor type</label>
        <select id="fHist" value={filters.histology} onChange={(e) => onChange({ histology: e.target.value })}>
          <option value="">All</option>
          {histologies.map((h) => <option key={h} value={h}>{h}</option>)}
        </select>
      </div>

      <div className="field">
        <label htmlFor="fTier">Company size</label>
        <select id="fTier" value={filters.tier} onChange={(e) => onChange({ tier: e.target.value })}>
          <option value="">All</option>
          {COMPANY_TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      <div className="field">
        <label htmlFor="fArea">Area</label>
        <select id="fArea" value={filters.area} onChange={(e) => onChange({ area: e.target.value })}>
          <option value="">All</option>
          <option>Solid</option>
          <option>Heme</option>
        </select>
      </div>

      <div className="field">
        <label htmlFor="fType">Catalyst</label>
        <select id="fType" value={filters.type} onChange={(e) => onChange({ type: e.target.value })}>
          <option value="">All</option>
          {CATALYST_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      <div className="field">
        <label htmlFor="fSignal">Signal</label>
        <select id="fSignal" value={filters.signal} onChange={(e) => onChange({ signal: e.target.value })}>
          <option value="">All</option>
          {SIGNALS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {conferences.length > 0 && (
        <div className="field">
          <label htmlFor="fMeet">Meeting</label>
          <select id="fMeet" value={filters.meeting} onChange={(e) => onChange({ meeting: e.target.value })}>
            <option value="">All</option>
            {conferences.map((c) => <option key={c.id} value={c.id}>{c.label || c.name}</option>)}
          </select>
        </div>
      )}

      <div className="field">
        <label htmlFor="fWithin">Within</label>
        <select id="fWithin" value={filters.within} onChange={(e) => onChange({ within: e.target.value })}>
          <option value="">Any time</option>
          <option value="30">Next 30 days</option>
          <option value="90">Next 90 days</option>
          <option value="180">Next 6 months</option>
        </select>
      </div>

      <div className="field">
        <label htmlFor="fSort">Sort</label>
        <select id="fSort" value={sort} onChange={(e) => onSort(e.target.value as TrackerSort)}>
          <option value="date">Date</option>
          <option value="impact">Impact</option>
        </select>
      </div>

      <div className="field">
        <label htmlFor="fView">Saved view</label>
        <select id="fView" value={savedViewValue} onChange={(e) => onSelectSavedView(e.target.value)}>
          <option value="">None</option>
          {savedViews.map((v, i) => <option key={v.name} value={`v${i}`}>{v.name}</option>)}
          <option value="__save">Save current view…</option>
          {savedViews.length > 0 && <option value="__del">Delete selected view…</option>}
        </select>
      </div>

      <div className="field grow">
        <label htmlFor="fQ">Search</label>
        <input
          type="search"
          id="fQ"
          placeholder="Company, asset, target, study, NCT"
          value={filters.q}
          onChange={(e) => onChange({ q: e.target.value })}
        />
        {filters.q && (
          <button type="button" className="qclear" aria-label="Clear search" title="Clear search" onClick={() => onChange({ q: '' })}>&times;</button>
        )}
      </div>

      <button type="button" className="ib tip-r" title="Clear all filters" aria-label="Clear all filters" onClick={onReset}>
        <TrackerIcon name="reset" />
      </button>
    </section>
  );
};

export default CatalystFilterBar;
